import type { Router } from "vue-router";
import { useAuthStore } from "../store/auth";
import { queryClient } from "@/queryClient";
import {
  getSideNavigationQueryKey,
  normalizeSidebarRoutePath,
  type SideNavigationQueryResult,
} from "@/composables/useSideNavigationQuery";
import {
  buildRouteAccessCandidates,
  canAccessPathFromContext,
  getBestDefaultRouteFromContext,
  isAlwaysAllowedRoute,
  shouldUseClientDefaultFallback,
} from "@/utils/accessControl";

const CLIENT_DEFAULT_ROUTE_PATHS = [
  "/overview",
  "/client/dashboard-v2",
  "/onboarding",
  "/updates",
  "/apps/task-dashboard",
  "/communication/chat",
  "/leads",
  "/reports",
];

const LAST_ROUTE_KEY = "dmsuite:last_route_after_logout";
const TASK_ONLY_LEVELS = new Set(["2", "3", "6", "8", "9"]);
const DASHBOARD_USER_TYPES = new Set(["2", "3"]);
const BLOCKED_LEVELS = new Set(["4", "5", "7"]);
const TASK_ONLY_DEFAULT_ROUTE = "/apps/task-dashboard";
const PRIMARY_INBOX_ROUTE = "/inbox";
const LEGACY_INBOX_ROUTE = "/emails";
const USERS_MANAGEMENT_ROUTE = "/users";
const INBOX_ROUTES = new Set([LEGACY_INBOX_ROUTE, PRIMARY_INBOX_ROUTE]);

function buildAuthOwnerKey(auth: ReturnType<typeof useAuthStore>): string {
  if (auth.accountType === "internal_user") {
    return `internal:${
      auth.internalUser?.id || auth.internalUser?.email || ""
    }`;
  }

  if (auth.accountType === "client") {
    return `client:${
      auth.clientProfile?.id ||
      auth.clientProfile?.client_id ||
      auth.clientProfile?.email ||
      ""
    }`;
  }

  return "";
}

function normalizeInboxRoute(path: string): string {
  const normalizedPath = String(path || "").trim();

  if (!normalizedPath) {
    return "";
  }

  const queryIndex = normalizedPath.search(/[?#]/);
  const pathname =
    queryIndex >= 0 ? normalizedPath.slice(0, queryIndex) : normalizedPath;
  const suffix = queryIndex >= 0 ? normalizedPath.slice(queryIndex) : "";

  if (pathname === LEGACY_INBOX_ROUTE) {
    return `${PRIMARY_INBOX_ROUTE}${suffix}`;
  }

  return normalizedPath;
}

export function rememberRouteForNextLogin(
  path: string,
  auth: ReturnType<typeof useAuthStore>
): void {
  const normalized = normalizeInboxRoute(path);
  if (!normalized || normalized === "/") return;

  const owner = buildAuthOwnerKey(auth);
  if (!owner) return;

  localStorage.setItem(
    LAST_ROUTE_KEY,
    JSON.stringify({
      path: normalized,
      owner,
    })
  );
}

export function consumeRememberedRoute(
  auth: ReturnType<typeof useAuthStore>
): string {
  const remembered = localStorage.getItem(LAST_ROUTE_KEY) || "";
  localStorage.removeItem(LAST_ROUTE_KEY);
  if (!remembered) return "";

  try {
    const parsed = JSON.parse(remembered) as {
      path?: string;
      owner?: string;
    };
    if (parsed.owner && parsed.owner === buildAuthOwnerKey(auth)) {
      return normalizeInboxRoute(String(parsed.path || ""));
    }
    return "";
  } catch {
    return "";
  }
}

export function isTaskOnlyInternalUser(
  auth: ReturnType<typeof useAuthStore>
): boolean {
  return (
    auth.accountType === "internal_user" &&
    TASK_ONLY_LEVELS.has(auth.userLevel) &&
    !DASHBOARD_USER_TYPES.has(auth.userRole)
  );
}

export function isBlockedInternalUser(
  auth: ReturnType<typeof useAuthStore>
): boolean {
  return (
    auth.accountType === "internal_user" && BLOCKED_LEVELS.has(auth.userLevel)
  );
}

export function isClientRestrictedRoute(path: string): boolean {
  return (
    path === USERS_MANAGEMENT_ROUTE ||
    path === "/clients" ||
    path.startsWith("/clients/") ||
    path.startsWith("/users/clients/") ||
    path === "/admin/manage-client-sidenav"
  );
}

export function canAccessInboxRoute(
  auth: ReturnType<typeof useAuthStore>
): boolean {
  if (auth.isSuperAdmin) {
    return true;
  }

  const contextualAccess = canAccessPathFromContext(
    PRIMARY_INBOX_ROUTE,
    auth.accessContext
  );

  if (contextualAccess !== null) {
    return auth.accountType === "internal_user" && contextualAccess;
  }

  return auth.accountType === "internal_user";
}

export function getClientFirstEntryRoute(auth: ReturnType<typeof useAuthStore>): string | null {
  if (auth.accountType !== "client" || !auth.clientProfile?.first_entry?.required) return null;
  return auth.clientProfile.first_entry.status === "plan_ready" ? "/client/initial-plan" : "/client/first-entry";
}

export function getDefaultRouteForAuth(
  auth: ReturnType<typeof useAuthStore>
): string {
  const firstEntryRoute = getClientFirstEntryRoute(auth);
  if (firstEntryRoute) return firstEntryRoute;
  if (auth.accountType === "client" && auth.clientProfile?.first_entry?.status === "completed" && canAccessRoute("/overview", auth)) return "/overview";
  if (
    auth.accountType !== "internal_user" &&
    auth.accountType !== "client"
  ) {
    return "";
  }

  if (
    auth.accountType === "client" &&
    shouldUseClientDefaultFallback(auth.accessContext)
  ) {
    return "/client-profile";
  }

  const contextualDefaultRoute = getBestDefaultRouteFromContext(
    auth.accessContext
  );

  let preferredRoutes: string[];
  if (isTaskOnlyInternalUser(auth)) {
    preferredRoutes = [contextualDefaultRoute, TASK_ONLY_DEFAULT_ROUTE];
  } else if (
    auth.accountType === "internal_user" &&
    canAccessInboxRoute(auth)
  ) {
    preferredRoutes = ["/overview", contextualDefaultRoute];
  } else {
    preferredRoutes = [contextualDefaultRoute, "/overview"];
  }

  const fallbackRoute =
    auth.accountType === "client" ? "/client-profile" : "/profile";
  const candidates = [
    ...preferredRoutes,
    ...(auth.accessContext?.sidebar.allowed_route_paths || []),
    fallbackRoute,
  ];

  return candidates.find((path) => path && canAccessRoute(path, auth)) || fallbackRoute;
}

export function canAccessRoute(
  path: string,
  auth: ReturnType<typeof useAuthStore>
): boolean {
  if (auth.accountType === "client" && isClientRestrictedRoute(path)) {
    return false;
  }

  if (path === "/client/first-entry" || path === "/client/initial-plan") return auth.accountType === "client";
  if (auth.isSuperAdmin) {
    return true;
  }

  if (isAlwaysAllowedRoute(path)) {
    return true;
  }

  if (
    auth.accountType === "client" &&
    shouldUseClientDefaultFallback(auth.accessContext) &&
    buildRouteAccessCandidates(path).some((candidate) =>
      CLIENT_DEFAULT_ROUTE_PATHS.includes(candidate)
    )
  ) {
    return true;
  }

  const contextualAccess = canAccessPathFromContext(path, auth.accessContext);
  if (contextualAccess !== null) {
    if (auth.accountType === "client" && path === USERS_MANAGEMENT_ROUTE) {
      return false;
    }

    return contextualAccess;
  }

  if (
    auth.accountType === "internal_user" &&
    path === USERS_MANAGEMENT_ROUTE &&
    !auth.isSuperAdmin
  ) {
    return false;
  }

  if (path === "/client/onboarding" && auth.accountType !== "client") {
    return false;
  }

  if (INBOX_ROUTES.has(path) && !canAccessInboxRoute(auth)) {
    return false;
  }

  if (auth.accountType === "client" && path.startsWith("/reports")) {
    const queryKey = getSideNavigationQueryKey(auth);
    const queryData =
      queryClient.getQueryData<SideNavigationQueryResult>(queryKey);
    if (queryData) {
      const hasReportsAccess = queryData.sections.some((section) =>
        (section.items || []).some((item) => {
          const normalizedRoute = normalizeSidebarRoutePath(item);
          return (
            normalizedRoute === "/reports" || normalizedRoute === "reports"
          );
        })
      );
      if (!hasReportsAccess) {
        return false;
      }
    }
  }

  if (!isTaskOnlyInternalUser(auth)) {
    return true;
  }

  return (
    path === TASK_ONLY_DEFAULT_ROUTE ||
    path.startsWith("/apps/tasks/") ||
    path === "/operations/task" ||
    path === "/reports"
  );
}

/**
 * After successful login, prefer inbox when available unless a remembered route exists.
 */
export function redirectAfterLogin(
  router: Router,
  auth: ReturnType<typeof useAuthStore>
): void {
  const id = auth.loginIdentifier || "";
  const isInternal = auth.accountType === "internal_user";
  const isClient = auth.accountType === "client";
  const rememberedRoute = consumeRememberedRoute(auth);

  const firstEntryRoute = getClientFirstEntryRoute(auth);
  if (firstEntryRoute) {
    router.push(firstEntryRoute);
    return;
  }
  if (isClient && auth.clientProfile?.first_entry?.status === "completed") {
    router.push(getDefaultRouteForAuth(auth));
    return;
  }

  if (isBlockedInternalUser(auth)) {
    throw new Error("ACCESS_DENIED_LEVEL");
  }

  if (rememberedRoute) {
    const canonicalRoute = normalizeInboxRoute(rememberedRoute);
    const targetRoute = canAccessRoute(canonicalRoute, auth)
      ? canonicalRoute
      : getDefaultRouteForAuth(auth);

    if (
      targetRoute.startsWith("http://") ||
      targetRoute.startsWith("https://")
    ) {
      window.location.href = targetRoute;
      return;
    }

    router.push(targetRoute);
    return;
  }

  if (isInternal && auth.internalUser) {
    router.push(getDefaultRouteForAuth(auth));
    return;
  }

  if (isClient && auth.clientProfile) {
    router.push(getDefaultRouteForAuth(auth));
    return;
  }

  if (!id) {
    throw new Error("ACCESS_DENIED");
  }

  throw new Error("ACCESS_DENIED");
}

/** Whether identifier is treated as internal staff (@proweaver) for UI labels */
export function isProweaverIdentifier(identifier: string): boolean {
  return identifier.toLowerCase().includes("@proweaver");
}
